import { or, not, and } from 'tio-ui/utilities';
import { PhGraduationCap, PhX, PhPencil } from 'ember-phosphor-icons';
import Button from 'tio-ui/components/button';
import Input from 'tio-ui/components/input';
import Textarea from 'tio-ui/components/textarea';
import ReSelect from 'tio-ui/components/select';
import inputmask from '../../../../modifiers/inputmask.ts';
import TuitionAssistanceFormsCoursesGradeSelectOptions from '../../../tuition-assistance/forms/courses/grade-select-options.gts';
import TuitionAssistanceProgramFormField from '../../../tuition-assistance/program-form-field.gts';
import TuitionAssistanceFormsCustomFieldsForProvince from '../../../tuition-assistance/forms/custom-fields-for-province.gts';
import ExpandableText from '../../../expandable-text.gts';
import type { TOC } from '@ember/component/template-only';
import type { TasCourseExtension as TasCourseModel } from 'tio-data/data/resources/tas-course.ext';
import type { TasProgramTemplateExtension as TasProgramTemplateModel } from 'tio-data/data/resources/tas-program-template.ext';
import { DrawerHeader, DrawerBody } from 'tio-ui/components/drawer';
import type { TASCourseModelFieldsSignature } from 'tio-data/data/resources/tas-course.type';
import type { ValidationErrorSignature } from '../../../tuition-assistance/forms/courses/grade-select-options.gts';
import type { CustomFieldSignature } from '../../../../types/tuition-assistance.ts';

/**
 * Course expense item for display
 */
export interface CourseExpenseItem {
  label: string;
  amount: string;
  fieldName?: string;
  isTotal: boolean;
}

/**
 * Course detail item for display
 */
export interface CourseDetailItem {
  label: string;
  value: string;
}

/**
 * Courses drawer content component
 * Displays course details with inline expense and detail editing capability
 */
export interface CoursesDrawerSignature {
  Args: {
    courses: TasCourseModel[];
    selectedCourseIndex: number;
    onCourseTabChange: (index: number) => void;

    // Display data
    courseExpensesFor: (course: TasCourseModel) => CourseExpenseItem[];
    courseDetailsFor: (course: TasCourseModel) => CourseDetailItem[];
    courseCustomFieldsFor: (course: TasCourseModel) => CustomFieldSignature[];
    getTabLabel: (index: number) => string;

    // Program template for field configuration
    programTemplate: TasProgramTemplateModel;

    // Permissions
    canEditCourse: boolean;
    /** When true, COURSE_TUITION is locked (Course Cash with reserved funds) */
    isCourseTuitionLocked: boolean;

    // Expense editing functionality
    isEditingExpenses: boolean;
    onToggleExpenseEdit: () => void;
    onSaveExpenses: () => void;
    onUpdateExpense: (fieldName: string, event: Event) => void;
    getCourseExpenseValue: (fieldName: string) => string;
    expenseChangelog: string;
    onExpenseChangelogUpdate: (value: string) => void;
    expenseChangelogErrors: string[];
    isExpenseFormValid: boolean;
    isExpensesSaving: boolean;

    // Detail editing functionality
    isEditingDetails: boolean;
    onToggleDetailEdit: () => void;
    onSaveDetails: () => void;
    onUpdateDetail: (fieldName: string, value: string | number) => void;
    onDidUpdateCourseCustomFields: (customFields: CustomFieldSignature[]) => void;
    getCourseDetailValue: (fieldName: string) => string;
    courseCustomFieldValidationErrors: ValidationErrorSignature[];
    detailChangelog: string;
    onDetailChangelogUpdate: (value: string) => void;
    detailChangelogErrors: string[];
    isDetailFormValid: boolean;
    isDetailsSaving: boolean;
    fieldsCopy: TASCourseModelFieldsSignature | null;
    formValidationErrors: ValidationErrorSignature[];
  };
}

/**
 * Configuration for editable detail fields
 * Defines which component to use for non-picklist fields
 */
const DETAIL_FIELD_CONFIGS = [
  { name: 'COURSE_NAME', component: 'Input', testId: 'detail-course-name' },
  { name: 'COURSE_NUMBER', component: 'Input', testId: 'detail-course-number' },
  { name: 'COURSE_DESCRIPTION', component: 'Textarea', testId: 'detail-course-description' },
  { name: 'COURSE_CREDIT', component: 'Input', testId: 'detail-course-credit' },
] as const;

/**
 * Configuration for editable expense fields
 * Defines field names for each expense type
 * Labels come from program template via TuitionAssistanceProgramFormField
 */
const EXPENSE_FIELD_CONFIGS = [
  { name: 'COURSE_TUITION', testId: 'tuition' },
  { name: 'COURSE_BOOKS', testId: 'books' },
  { name: 'COURSE_LABS', testId: 'labs' },
  { name: 'COURSE_SOFTWARE', testId: 'software' },
  { name: 'COURSE_FEES', testId: 'fees' },
] as const;

const CoursesDrawer: TOC<CoursesDrawerSignature> = <template>
  <DrawerHeader>
    <h2 class="tio-text-heading tio-fg-primary">Course Details</h2>
  </DrawerHeader>

  <DrawerBody>
    <div>
      {{#if @courses.length}}
        {{! Tab Bar }}
        <div class="tio-flex-center border-b-2 tio-border-default mb-6">
          <div class="tio-flex-extra-tight">
            {{#each @courses as |_course index|}}
              <button
                type="button"
                class="px-4 py-2 tio-text-sm-semibold uppercase tracking-wide transition-colors
                  {{if
                    (eq index @selectedCourseIndex)
                    'tio-fg-brand border-b-2 tio-border-brand'
                    'tio-fg-muted hover:tio-fg-secondary'
                  }}"
                {{on "click" (fn @onCourseTabChange index)}}
                data-test-course-tab={{index}}
              >
                {{@getTabLabel index}}
              </button>
            {{/each}}
          </div>
        </div>

        {{! Content }}
        {{#each @courses as |course index|}}
          {{#if (eq index @selectedCourseIndex)}}
            <div class="tio-col-extra-loose px-6 py-7">
              {{! Expenses Section with Inline Editing }}
              <div class="tio-col-flush">
                {{! Section Header }}
                <div
                  class="tio-row-between px-4 py-2.5 tio-bg-sunken border tio-border-default rounded-[10px] mb-0"
                >
                  <span class="tio-text-base-semibold tio-fg-primary">Expenses</span>
                  {{#if @canEditCourse}}
                    <Button
                      @appearance="minimal"
                      @size="sm"
                      @icon={{if @isEditingExpenses PhX PhPencil}}
                      {{on "click" @onToggleExpenseEdit}}
                      data-test-edit-expenses-button
                      class="!p-1.5"
                    />
                  {{/if}}
                </div>

                {{! Expense Rows }}
                <div class="mt-2 space-y-2">
                  {{#if @isEditingExpenses}}
                    {{! Edit Mode - Show Input Fields }}
                    <div class="tio-col-loose p-4">
                      {{#each EXPENSE_FIELD_CONFIGS as |expenseConfig|}}
                        <TuitionAssistanceProgramFormField
                          @name={{expenseConfig.name}}
                          @programTemplate={{@programTemplate}}
                          as |field|
                        >
                          {{#if
                            (and @isCourseTuitionLocked (eq expenseConfig.name "COURSE_TUITION"))
                          }}
                            <Input
                              id={{field.inputId}}
                              @value={{@getCourseExpenseValue expenseConfig.name}}
                              @size="sm"
                              data-test-expense-input={{expenseConfig.testId}}
                              @isDisabled={{true}}
                              {{inputmask
                                alias="currency"
                                prefix="$"
                                unmaskAsNumber=true
                                digits="2"
                              }}
                            />
                          {{else}}
                            <Input
                              id={{field.inputId}}
                              @value={{@getCourseExpenseValue expenseConfig.name}}
                              @size="sm"
                              data-test-expense-input={{expenseConfig.testId}}
                              {{inputmask
                                alias="currency"
                                prefix="$"
                                unmaskAsNumber=true
                                digits="2"
                              }}
                              {{on "change" (fn @onUpdateExpense expenseConfig.name)}}
                            />
                          {{/if}}
                        </TuitionAssistanceProgramFormField>
                      {{/each}}
                    </div>

                    {{! Changelog Comment Field }}
                    <div class="px-4 pt-4">
                      <label class="flex tio-text-sm-semibold tio-fg-secondary mb-1">
                        Comment
                        <span class="tio-fg-danger ml-1">*</span>
                      </label>
                      <Textarea
                        @value={{@expenseChangelog}}
                        @onChange={{@onExpenseChangelogUpdate}}
                        @errors={{@expenseChangelogErrors}}
                        @size="sm"
                        data-test-expense-changelog
                        class="w-full"
                      />
                    </div>

                    {{! Save Changes Button }}
                    <div class="tio-flex-end mt-4 px-4">
                      <Button
                        @intent="primary"
                        @appearance="outlined"
                        @size="sm"
                        @isRunning={{@isExpensesSaving}}
                        {{on "click" @onSaveExpenses}}
                        disabled={{or @isExpensesSaving (not @isExpenseFormValid)}}
                        data-test-save-expenses-button
                      >
                        {{if @isExpensesSaving "Saving..." "Save Changes"}}
                      </Button>
                    </div>
                  {{else}}
                    {{! Read Mode - Show Static Values }}
                    {{#each (@courseExpensesFor course) as |expense|}}
                      <div
                        class="tio-row-between p-4
                          {{if expense.isTotal '' 'border-b tio-border-default'}}"
                      >
                        <span
                          class="text-base
                            {{if
                              expense.isTotal
                              'font-bold tio-fg-secondary'
                              'font-medium tio-fg-secondary'
                            }}"
                        >{{expense.label}}</span>
                        <span
                          class="text-base
                            {{if
                              expense.isTotal
                              'font-semibold tio-fg-brand'
                              'font-medium tio-fg-secondary'
                            }}"
                        >{{expense.amount}}</span>
                      </div>
                    {{/each}}
                  {{/if}}
                </div>
              </div>

              {{! Details Section }}
              <div class="tio-col-flush">
                {{! Section Header }}
                <div
                  class="tio-row-between px-4 py-2.5 tio-bg-sunken border tio-border-default rounded-[10px] mb-0"
                >
                  <span class="tio-text-base-semibold tio-fg-primary">Details</span>
                  {{#if @canEditCourse}}
                    <Button
                      @appearance="minimal"
                      @size="sm"
                      @icon={{if @isEditingDetails PhX PhPencil}}
                      {{on "click" @onToggleDetailEdit}}
                      data-test-edit-details-button
                      class="!p-1.5"
                    />
                  {{/if}}
                </div>

                {{#if @isEditingDetails}}
                  {{! Editing Detail Fields }}
                  <div class="tio-col-loose p-4">
                    {{#each DETAIL_FIELD_CONFIGS as |fieldConfig|}}
                      <TuitionAssistanceProgramFormField
                        @name={{fieldConfig.name}}
                        @programTemplate={{@programTemplate}}
                        as |field|
                      >
                        {{#if field.isFieldOptionPicklist}}
                          <ReSelect
                            data-test={{fieldConfig.testId}}
                            @items={{field.fieldOptions}}
                            @selected={{@getCourseDetailValue fieldConfig.name}}
                            @onChange={{fn @onUpdateDetail fieldConfig.name}}
                            @size="sm"
                            @clearable={{false}}
                          />
                        {{else if (eq fieldConfig.component "Textarea")}}
                          <Textarea
                            data-test={{fieldConfig.testId}}
                            id={{field.inputId}}
                            @value={{@getCourseDetailValue fieldConfig.name}}
                            @onChange={{fn @onUpdateDetail fieldConfig.name}}
                            @size="sm"
                          />
                        {{else}}
                          <Input
                            data-test={{fieldConfig.testId}}
                            id={{field.inputId}}
                            @value={{@getCourseDetailValue fieldConfig.name}}
                            @onChange={{fn @onUpdateDetail fieldConfig.name}}
                            @size="sm"
                          />
                        {{/if}}
                      </TuitionAssistanceProgramFormField>
                    {{/each}}

                    {{! Course Grade - Use GradeSelectOptions component }}
                    {{#if @fieldsCopy}}
                      <TuitionAssistanceFormsCoursesGradeSelectOptions
                        @programTemplate={{@programTemplate}}
                        @updateCourseGrade={{fn @onUpdateDetail "COURSE_GRADE"}}
                        @formValidationErrors={{@formValidationErrors}}
                        @fieldsCopy={{@fieldsCopy}}
                      />
                    {{/if}}

                    {{! Custom Course Detail Fields - Province-Based Component }}
                    <TuitionAssistanceFormsCustomFieldsForProvince
                      @province="COURSE"
                      @customFields={{@courseCustomFieldsFor course}}
                      @didUpdateFields={{@onDidUpdateCourseCustomFields}}
                      @formValidationErrors={{@courseCustomFieldValidationErrors}}
                      @programTemplate={{@programTemplate}}
                    />

                    {{! Detail Changelog }}
                    <div>
                      <label class="flex tio-text-sm-semibold tio-fg-secondary mb-1">
                        Comment
                        <span class="tio-fg-danger ml-1">*</span>
                      </label>
                      <Textarea
                        @value={{@detailChangelog}}
                        @onChange={{@onDetailChangelogUpdate}}
                        @errors={{@detailChangelogErrors}}
                        @size="sm"
                        data-test-detail-changelog
                        class="w-full"
                      />
                    </div>

                    {{! Save Details Button }}
                    <div class="tio-flex-end mt-2">
                      <Button
                        @intent="primary"
                        @appearance="outlined"
                        @size="sm"
                        @isRunning={{@isDetailsSaving}}
                        {{on "click" @onSaveDetails}}
                        disabled={{or @isDetailsSaving (not @isDetailFormValid)}}
                        data-test-save-details-button
                      >
                        {{if @isDetailsSaving "Saving..." "Save Changes"}}
                      </Button>
                    </div>
                  </div>
                {{else}}
                  {{! Detail Rows (Read-only) }}
                  {{#each (@courseDetailsFor course) as |detail|}}
                    <div
                      class="tio-col-flush gap-0.5 p-4 border-b tio-border-default last:border-b-0"
                    >
                      <span
                        class="tio-text-base-semibold tio-fg-secondary leading-snug"
                      >{{detail.label}}</span>
                      <ExpandableText
                        @text={{detail.value}}
                        class="tio-text-base-medium tio-fg-muted leading-snug"
                      />
                    </div>
                  {{/each}}
                {{/if}}
              </div>
            </div>
          {{/if}}
        {{/each}}
      {{else}}
        <div class="p-6 text-center">
          <PhGraduationCap @size="48" class="tio-fg-muted mx-auto mb-4" />
          <p class="tio-fg-muted">No courses found for this application</p>
        </div>
      {{/if}}
    </div>
  </DrawerBody>
</template>;

export default CoursesDrawer;
